
import { z } from 'zod';

export type AllowedStatus = "registered" | "active" | "terminated" | "cancelled";

// Define a type for the service object
export interface ServiceFormValue {
  service_id: string;
  price: number;
  selected: boolean;
}

// We need to create a schema based on whether services should be shown
export const createBaseSchema = (allowedStatuses: AllowedStatus[]) => {
  return z.object({
    customer_id: z.string().uuid({ message: "Please select a customer" }),
    installation_street: z.string().min(1, { message: "Street address is required" }),
    installation_zip_code: z.string().min(1, { message: "Zip code is required" }),
    installation_city: z.string().min(1, { message: "City is required" }),
    housing_type: z.enum(["house", "apartment", "office"], { 
      required_error: "Please select a housing type",
    }),
    activation_date: z.date().optional().nullable(),
    status: z.enum(allowedStatuses as [AllowedStatus, ...AllowedStatus[]], {
      required_error: "Please select a status",
    }),
    alarm_com_id: z.string().optional().nullable(),
    contact_name: z.string().optional().nullable(),
    contact_phone: z.string().optional().nullable(),
    contact_email: z.string().email({ message: "Invalid email format" }).optional().nullable(),
    prom_avarn: z.string().optional().nullable(),
    invoice_reference: z.string().optional().nullable(),
  });
}

// Create a schema with services
export const createSchemaWithServices = (allowedStatuses: AllowedStatus[]) => {
  return createBaseSchema(allowedStatuses).extend({
    services: z.array(
      z.object({
        service_id: z.string(),
        price: z.number().optional(),
        selected: z.boolean().default(false)
      })
    ).optional()
  });
}

// Create a schema without services
export const createSchemaWithoutServices = (allowedStatuses: AllowedStatus[]) => {
  return createBaseSchema(allowedStatuses);
}

// This will be used to infer the form values type
export const createSubscriptionFormSchema = (allowedStatuses: AllowedStatus[], showServices: boolean) => {
  return showServices ? createSchemaWithServices(allowedStatuses) : createSchemaWithoutServices(allowedStatuses);
}

export type BaseFormValues = z.infer<ReturnType<typeof createBaseSchema>>;
export type WithServicesFormValues = z.infer<ReturnType<typeof createSchemaWithServices>>;
export type WithoutServicesFormValues = z.infer<ReturnType<typeof createSchemaWithoutServices>>;

// Use union type instead of conditional type to avoid TypeScript error
export type SubscriptionFormValues = WithServicesFormValues | WithoutServicesFormValues;
